import React, { useMemo } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getFlyingBetslipConfig } from 'helpers/prematch';

import {
  get,
  getConfigProp,
  SelectedSportsbookSportProvider,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { COMPONENT_TYPES, sportsbookRoutes } from '@atlas/routing';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import SportsList from 'common/Widgets/SportsList/SportsList';
import TopEvents from 'common/Widgets/TopEvents';
import SportsbookFavorites from 'components/FavoriteEvents/SportsbookFavorites';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import DetailsPage from 'components/Pages/Details';
import EmptySportView from 'components/Pages/EmptySportView';
import LiveSportsbookLoader from 'components/Pages/LiveSportsbookLoader';
import SportLoader from 'components/Pages/Prematch/SportLoader';
import {
  SelectedEventQueryRouteWrapper,
  SelectedEventQueryViewWrapper,
  SelectedProviderWrapper,
} from 'components/Pages/SelectedWrappers';
import SportItemDestruction from 'components/Pages/SportItemDestruction';
import { DefaultSportsbookNavigation } from 'components/Pages/SportsbookNavigation';
import { CategoryRedirect, SportRedirect } from 'components/Pages/SportsbookRedirects';

import LiveCategoriesView from './LiveCategoriesView';

const LiveSportsbookView = () => {
  const { quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();
  const location = useLocation();
  const { pathname } = location;
  const [locationPage, locationSportName, locationCategoryName] = pathname
    .split('/')
    .filter(Boolean);

  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const page = SiteConfigManager.getComponentOptions(COMPONENT_TYPES.SPORT_LIVE);
  const areTopEventsAvailable = get(page, 'topEvents', true);
  const sportLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_LIVE);

  const layoutClassName = useMemo(
    () =>
      cx('layout-to-scroll full-height', {
        'layout-to-scroll-with-padding': getFlyingBetslipConfig().hasExtended,
        quickbet: isQuickBet || betIdToEdit,
      }),
    [betIdToEdit, isQuickBet],
  );

  const content = (
    <LiveSportsbookLoader>
      <div className={layoutClassName}>
        {!isSportRadarProject && <FreeBetNotification />}
        {areTopEventsAvailable && <TopEvents />}

        <Switch>
          <Route path={`${sportLivePath}/:sportName/:categoryName?`}>
            <DefaultSportsbookNavigation path={sportLivePath}>
              <h1 className="d-none">
                {locationPage} {locationSportName} {locationCategoryName}
              </h1>
              <span className="sport-scroll" />
              <SportsList />

              <div className="sportsbook-content">
                <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
                  <Switch>
                    <Route
                      component={SportsbookFavorites}
                      path={`${sportLivePath}/${sportsbookRoutes.favorites}`}
                    />

                    <EmptySportView>
                      <Route path={`${sportLivePath}/:sportName/:categoryName/:tournamentName?`}>
                        <SportLoader className="absolute">
                          <Route
                            component={LiveCategoriesView}
                            exact
                            path={`${sportLivePath}/:sportName/:categoryName`}
                          />
                        </SportLoader>
                      </Route>
                      <Route>
                        <CategoryRedirect />
                        <SportItemDestruction />
                      </Route>
                    </EmptySportView>
                  </Switch>
                </SelectedProviderWrapper>
              </div>
            </DefaultSportsbookNavigation>
          </Route>
          <SportRedirect />
        </Switch>
      </div>
    </LiveSportsbookLoader>
  );

  return (
    <SelectedEventQueryViewWrapper fallback={content} location={location}>
      <SelectedEventQueryRouteWrapper>
        <DetailsPage />
      </SelectedEventQueryRouteWrapper>
    </SelectedEventQueryViewWrapper>
  );
};

export default LiveSportsbookView;
